<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import PageHeader from '../../SettingsSubPageHeader.vue';
import router from '../../../../index';

const PROVIDERS = ['atendesimples'];

// Mirrors the format validation on Channel::Voice, so a number the model would
// reject never reaches the API.
const isE164 = (value = '') => /^\+[1-9]\d{1,14}$/.test(value);

export default {
  components: {
    PageHeader,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      phoneNumber: '',
      provider: PROVIDERS[0],
      providers: PROVIDERS,
      apiKey: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  validations: {
    inboxName: { required },
    phoneNumber: { required, isE164 },
    provider: { required },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const voiceChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.inboxName,
            channel: {
              type: 'voice',
              phone_number: this.phoneNumber,
              provider: this.provider,
              ...(this.apiKey ? { settings: { api_key: this.apiKey } } : {}),
            },
          }
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: voiceChannel.id,
          },
        });
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.VOICE_CHANNEL.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div
    class="border border-slate-25 dark:border-slate-800/60 bg-white dark:bg-slate-900 h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0"
  >
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.VOICE_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.VOICE_CHANNEL.DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="createChannel()">
      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label :class="{ error: v$.inboxName.$error }">
          {{ $t('INBOX_MGMT.ADD.VOICE_CHANNEL.INBOX_NAME.LABEL') }}
          <input
            v-model.trim="inboxName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.VOICE_CHANNEL.INBOX_NAME.PLACEHOLDER')
            "
            @blur="v$.inboxName.$touch"
          />
          <span v-if="v$.inboxName.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.VOICE_CHANNEL.INBOX_NAME.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label :class="{ error: v$.phoneNumber.$error }">
          {{ $t('INBOX_MGMT.ADD.VOICE_CHANNEL.PHONE_NUMBER.LABEL') }}
          <input
            v-model.trim="phoneNumber"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.VOICE_CHANNEL.PHONE_NUMBER.PLACEHOLDER')
            "
            @blur="v$.phoneNumber.$touch"
          />
          <span v-if="v$.phoneNumber.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.VOICE_CHANNEL.PHONE_NUMBER.ERROR') }}
          </span>
          <span v-else class="text-xs text-slate-500 dark:text-slate-400">
            {{ $t('INBOX_MGMT.ADD.VOICE_CHANNEL.PHONE_NUMBER.HELP') }}
          </span>
        </label>
      </div>

      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label :class="{ error: v$.provider.$error }">
          {{ $t('INBOX_MGMT.ADD.VOICE_CHANNEL.PROVIDER.LABEL') }}
          <select v-model="provider">
            <option v-for="option in providers" :key="option" :value="option">
              {{ $t(`INBOX_MGMT.ADD.VOICE_CHANNEL.PROVIDER.${option}`) }}
            </option>
          </select>
        </label>
      </div>

      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label>
          {{ $t('INBOX_MGMT.ADD.VOICE_CHANNEL.API_KEY.LABEL') }}
          <input
            v-model.trim="apiKey"
            type="password"
            autocomplete="off"
            :placeholder="
              $t('INBOX_MGMT.ADD.VOICE_CHANNEL.API_KEY.PLACEHOLDER')
            "
          />
          <span class="text-xs text-slate-500 dark:text-slate-400">
            {{ $t('INBOX_MGMT.ADD.VOICE_CHANNEL.API_KEY.HELP') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <woot-submit-button
          :loading="uiFlags.isCreating"
          :button-text="$t('INBOX_MGMT.ADD.VOICE_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
